/**
 * GET `GET {VITE_API_BASE_URL}/jain_lexicon/users/` — staff JWT + tenant (admin user directory).
 */
import { getStaffAccessToken } from "./authApi";
import { formatApiErrorBody } from "./formatApiError";
import { JAIN_LEXICON_GROUP } from "@jl/lib/jainLexiconRbac";

export const LEXICON_USERS_PATH = "/jain_lexicon/users/";

export type LexiconTableRole = "admin" | "scholar" | "contributor";

export type LexiconDirectoryUser = {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  is_active: boolean;
  created_at: string;
  contribution_count: number;
  lexicon_role: LexiconTableRole;
};

function jlApiBase(): string {
  const raw =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_API_BASE_URL as string | undefined)
      : undefined;
  return (raw ?? "").replace(/\/$/, "");
}

function jlTenantHeaders(): Record<string, string> {
  const id =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const trimmed = (id ?? "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    return {};
  }
  return { "X-Tenant-Id": trimmed };
}

function coerceList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    );
  }
  if (!raw || typeof raw !== "object") {
    return [];
  }
  const o = raw as Record<string, unknown>;
  for (const key of ["results", "data", "content"] as const) {
    const arr = o[key];
    if (Array.isArray(arr)) {
      return arr.filter(
        (x): x is Record<string, unknown> =>
          typeof x === "object" && x !== null && !Array.isArray(x)
      );
    }
  }
  return [];
}

function listNextPageUrl(apiBase: string, raw: unknown): string | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const root = raw as Record<string, unknown>;
  let next: unknown = root.next;
  if (next == null && root.links && typeof root.links === "object") {
    next = (root.links as Record<string, unknown>).next;
  }
  if (next === null || next === undefined || typeof next !== "string") {
    return null;
  }
  const trimmed = next.trim();
  if (!trimmed || trimmed.toLowerCase() === "null") {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const originMatch = /^(https?:\/\/[^/]+)/i.exec(apiBase);
  const origin = originMatch?.[1];
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (origin) {
    return `${origin}${path}`;
  }
  return `${apiBase.replace(/\/$/, "")}${path}`;
}

async function parseHttpError(res: Response): Promise<string> {
  const fallback = `Request failed (${res.status})`;
  try {
    const t = await res.text();
    const trimmed = t.trim();
    if (!trimmed) {
      return fallback;
    }
    try {
      const j = JSON.parse(trimmed) as unknown;
      return formatApiErrorBody(j) ?? fallback;
    } catch {
      return trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed;
    }
  } catch {
    return fallback;
  }
}

function pickStr(r: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = r[k];
    if (v == null) {
      continue;
    }
    const s = String(v).trim();
    if (s.length) {
      return s;
    }
  }
  return null;
}

function groupNamesLowerFromRow(r: Record<string, unknown>): string[] {
  const names: string[] = [];
  const groups = r.groups;
  if (Array.isArray(groups)) {
    for (const item of groups) {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const n = (item as Record<string, unknown>).name;
        if (typeof n === "string" && n.trim()) {
          names.push(n.toLowerCase());
        }
      }
    }
  }
  const single =
    pickStr(r, "permission_group", "lexicon_group", "group_name") ?? "";
  if (single.trim()) {
    names.push(single.toLowerCase());
  }
  const singleObj = r.group;
  if (
    singleObj &&
    typeof singleObj === "object" &&
    !Array.isArray(singleObj)
  ) {
    const n = (singleObj as Record<string, unknown>).name;
    if (typeof n === "string" && n.trim()) {
      names.push(n.toLowerCase());
    }
  }
  return names;
}

function resolveLexiconRole(r: Record<string, unknown>): LexiconTableRole {
  if (isLexiconAdminRow(r)) {
    return "admin";
  }

  const apiRole = (pickStr(r, "role") ?? "").toLowerCase();
  if (apiRole === "tenant_scholar") {
    return "scholar";
  }
  if (apiRole === "tenant_contributor") {
    return "contributor";
  }

  const names = groupNamesLowerFromRow(r);

  for (const g of names) {
    if (g.includes("scholar")) {
      return "scholar";
    }
  }

  const appRole = (
    pickStr(r, "app_role", "portal_role", "lexicon_role", "account_type") ??
    ""
  ).toLowerCase();
  if (appRole === "admin") {
    return "admin";
  }
  if (appRole === "scholar") {
    return "scholar";
  }
  if (appRole === "contributor") {
    return "contributor";
  }

  for (const g of names) {
    if (
      g.includes("contributer") ||
      g.includes("contributor") ||
      g.includes("jain_lexicon_contributer")
    ) {
      return "contributor";
    }
  }

  if (names.length > 0) {
    return "contributor";
  }

  const panelRole = (pickStr(r, "panel_role") ?? "").toLowerCase();
  if (panelRole === "admin") {
    return "admin";
  }
  if (panelRole === "scholar") {
    return "scholar";
  }
  if (panelRole === "contributor") {
    return "contributor";
  }

  return "contributor";
}

function isLexiconAdminRow(r: Record<string, unknown>): boolean {
  const apiRole = (pickStr(r, "role") ?? "").toLowerCase();
  if (apiRole === "tenant_admin") {
    return true;
  }

  for (const g of groupNamesLowerFromRow(r)) {
    if (g.includes("jain_lexicon_admin") || g === JAIN_LEXICON_GROUP.admin) {
      return true;
    }
  }

  const app = (pickStr(r, "app_role", "portal_role") ?? "").toLowerCase();
  if (app === "admin") {
    return true;
  }

  return false;
}

function coerceDirectoryUserActive(raw: unknown): boolean {
  if (raw === false || raw === "false" || raw === 0 || raw === "0") {
    return false;
  }
  if (
    typeof raw === "string" &&
    raw.trim().toLowerCase() === "inactive"
  ) {
    return false;
  }
  return true;
}

function mapRow(raw: Record<string, unknown>): LexiconDirectoryUser | null {
  const r =
    raw.user && typeof raw.user === "object" && !Array.isArray(raw.user)
      ? ({ ...(raw.user as Record<string, unknown>), ...raw } as Record<
          string,
          unknown
        >)
      : raw;

  const userId =
    pickStr(r, "user_id", "id") ?? pickStr(r, "uuid", "pk") ?? "";
  if (!userId.trim()) {
    return null;
  }

  const id = pickStr(r, "id") ?? userId;

  const email = pickStr(r, "email");
  const displayName =
    pickStr(r, "full_name", "display_name") ||
    [
      pickStr(r, "first_name") ?? "",
      pickStr(r, "last_name") ?? "",
    ]
      .join(" ")
      .trim() ||
    pickStr(r, "tenant_username") ||
    (email ? email.split("@")[0] : null);

  const isActive = coerceDirectoryUserActive(r.is_active ?? r.active);

  const createdRaw = pickStr(
    r,
    "created_on",
    "created_at",
    "date_joined",
    "joined_at"
  );
  const created =
    createdRaw ?? pickStr(r, "updated_on") ?? new Date().toISOString();

  let contributionCount = 0;
  const cc = r.contribution_count ?? r.contributions_count ?? r.term_count;
  if (typeof cc === "number" && Number.isFinite(cc)) {
    contributionCount = cc;
  }

  return {
    id,
    user_id: userId,
    email: email ?? null,
    display_name: displayName,
    is_active: isActive,
    created_at: created,
    contribution_count: contributionCount,
    lexicon_role: resolveLexiconRole(r),
  };
}

export async function fetchLexiconDirectoryUsers(): Promise<{
  users: LexiconDirectoryUser[];
  error: string | null;
}> {
  const base = jlApiBase();
  const token = getStaffAccessToken();
  if (!base) {
    return { users: [], error: "Missing VITE_API_BASE_URL" };
  }
  if (!token) {
    return { users: [], error: "Sign in with a staff account to load users." };
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...jlTenantHeaders(),
    Authorization: `Bearer ${token}`,
  };

  const merged: LexiconDirectoryUser[] = [];
  const seen = new Set<string>();
  let url: string | null = `${base}${LEXICON_USERS_PATH}`;
  const maxPages = 200;
  let pages = 0;

  while (url && pages < maxPages) {
    const res = await fetch(url, { method: "GET", headers });
    if (!res.ok) {
      return {
        users: merged.length > 0 ? merged : [],
        error: await parseHttpError(res),
      };
    }

    let raw: unknown;
    try {
      raw = await res.json();
    } catch {
      return {
        users: merged.length > 0 ? merged : [],
        error: "Invalid JSON from users API",
      };
    }

    const list = coerceList(raw);
    for (const rec of list) {
      const row = mapRow(rec);
      if (!row) {
        continue;
      }
      const key = row.user_id || row.id;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      merged.push(row);
    }

    url = listNextPageUrl(base, raw);
    pages += 1;
  }

  return { users: merged, error: null };
}

/** PATCH directory row `is_active` (staff JWT + tenant). Tries `row.id` then `row.user_id` if the first URL fails. */
export async function updateLexiconDirectoryUserActive(
  row: LexiconDirectoryUser,
  isActive: boolean
): Promise<{ ok: boolean; error: string | null }> {
  const base = jlApiBase();
  const token = getStaffAccessToken();
  if (!base) {
    return { ok: false, error: "Missing VITE_API_BASE_URL" };
  }
  if (!token) {
    return { ok: false, error: "Sign in with a staff account to update users." };
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...jlTenantHeaders(),
    Authorization: `Bearer ${token}`,
  };

  const ids = [...new Set([row.id, row.user_id].filter(Boolean))];
  let lastErr: string | null = "Could not update user.";

  for (const id of ids) {
    const url = `${base}${LEXICON_USERS_PATH}${encodeURIComponent(id)}/`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ is_active: isActive }),
    });
    if (res.ok || res.status === 204) {
      return { ok: true, error: null };
    }
    lastErr = await parseHttpError(res);
  }

  return { ok: false, error: lastErr };
}

/**
 * Same request this page uses (for Postman / docs). Token is not exposed.
 */
export function getLexiconUsersRequestSpec(): {
  method: "GET";
  url: string;
  headers: Record<string, string>;
} {
  const base = jlApiBase();
  const url = base ? `${base}${LEXICON_USERS_PATH}` : LEXICON_USERS_PATH;
  const tid =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const tenant = (tid ?? "").replace(/^\uFEFF/, "").trim();
  return {
    method: "GET",
    url,
    headers: {
      Authorization: "Bearer <staff_access_token>",
      Accept: "application/json",
      "X-Tenant-Id": tenant || "(not set — VITE_X_TENANTID)",
    },
  };
}
